/**
 * ToolAuditLogPopup – Dialog showing tool audit logs.
 * Supports ticket mode (filters by selected threads) and thread mode (single thread).
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Autocomplete, {
  createFilterOptions,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
} from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import type { SxProps, Theme } from "@mui/material/styles";

export interface ToolAuditLogRowLike {
  id: string | number;
  start_timestamp?: string | null;
  end_timestamp?: string | null;
  user_email?: string | null;
  action_permission?: string | null;
  tool_name: string;
  param_json?: unknown;
  output_json?: unknown;
  error_message?: string | null;
  reasoning?: string | null;
  audit_event_type?: "playbook_context";
  audit_turn_id?: string | null;
  [key: string]: unknown;
}

interface TicketThreadOption {
  ai_thread_id: number;
  created_at: string | null;
}

export interface ToolAuditChatApi {
  getTicketThreads: (ticketId: string) => Promise<any>;
  getTicketToolAuditLogs: (ticketId: string) => Promise<any>;
  getToolAuditLogs: (aiThreadId: string) => Promise<any>;
  refreshTicketAuditCache: (ticketId: string | null) => void;
  invalidateThreadAuditCache: (aiThreadId: string | null) => void;
}

interface ToolAuditLogPopupHelpers {
  scrollSx: SxProps<Theme>;
  formatTimestamp: (timestamp: any) => string;
  formatDuration: (startTimestamp: any, endTimestamp: any) => string;
  formatThreadTimestamp: (
    createdAt: any,
    threadOptions: TicketThreadOption[],
  ) => string;
  formatParamsAsKeyValue: (params: any) => string;
  getFullOutputString: (output: any) => string;
  isErrorRow: (row: any) => boolean;
  getActionTypeLabel: (row: any) => string;
  downloadAuditLogCsv: (rows: any[], filenamePrefix: string) => void;
}

interface ToolAuditLogPopupDependencies {
  chatApi: ToolAuditChatApi;
  helpers: ToolAuditLogPopupHelpers;
  getDropdownSafeDialogSx: (
    paperOverrides?: Record<string, unknown>,
  ) => Record<string, unknown>;
}

export interface ToolAuditLogPopupProps {
  open: boolean;
  onClose: () => void;
  aiThreadId: string | null;
  ticketId?: string | null;
  /** When set, shows a "Live debug" button that moves the logs into the live sidebar. */
  onLiveDebug?: () => void;
}

type TypeFilterValue = "all" | "auto" | "approval";
type StatusFilterValue = "all" | "SUCCESS" | "ERROR";

interface DropdownOption {
  value: string;
  label: string;
}

const filterDropdownOptions = createFilterOptions<DropdownOption>({
  stringify: (option) => `${option.label} ${option.value}`,
});

const AUDIT_MODAL_BASE_Z_INDEX = 14020;

export const createToolAuditLogPopup = ({
  chatApi,
  helpers,
  getDropdownSafeDialogSx,
}: ToolAuditLogPopupDependencies): React.FC<ToolAuditLogPopupProps> => {
  const {
    scrollSx,
    formatTimestamp,
    formatDuration,
    formatThreadTimestamp,
    formatParamsAsKeyValue,
    getFullOutputString,
    isErrorRow,
    getActionTypeLabel,
    downloadAuditLogCsv,
  } = helpers;

  const ToolAuditLogPopup: React.FC<ToolAuditLogPopupProps> = ({
    open,
    onClose,
    aiThreadId,
    ticketId,
    onLiveDebug,
  }) => {
    const blurActiveElement = () => {
      try {
        (document.activeElement as HTMLElement | null)?.blur?.();
      } catch {
        // no-op
      }
    };
    const auditHeaderSelectInputSx = {
      "& .MuiOutlinedInput-root": {
        height: 34,
        backgroundColor: "#fff",
        borderRadius: 1,
        color: "#19213D",
        fontSize: 13,
        px: 0.5,
        border: "1px solid #C4C4C4",
        boxShadow: "none",
        outline: "none",
        "&:hover": {
          borderColor: "#9A9A9A",
        },
        "&.Mui-focused": {
          borderColor: "#666F8D",
          boxShadow: "none",
          outline: "none",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          display: "none",
        },
      },
      "& .MuiInput-root": {
        boxShadow: "none",
        outline: "none",
        "&:before, &:after": {
          display: "none",
        },
      },
      "& input, & .MuiInputBase-input, & .MuiAutocomplete-input": {
        border: "none !important",
        outline: "none !important",
        boxShadow: "none !important",
        background: "transparent !important",
        paddingTop: "0 !important",
        paddingBottom: "0 !important",
        minHeight: "0 !important",
      },
    } as const;

    const auditTableFilterInputSx = {
      "& .MuiOutlinedInput-root": {
        height: 24,
        color: "#e6edf3",
        px: 0.5,
        py: 0,
        backgroundColor: "transparent",
        border: "1px solid #555",
        borderRadius: "4px",
        boxShadow: "none",
        outline: "none",
        "&:hover": {
          borderColor: "#777",
        },
        "&.Mui-focused": {
          borderColor: "#555",
          boxShadow: "none",
          outline: "none",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          display: "none",
        },
      },
      "& .MuiInput-root": {
        border: "1px solid #555",
        borderRadius: "4px",
        boxShadow: "none",
        outline: "none",
        "&:hover": {
          borderColor: "#777",
        },
        "&.Mui-focused": {
          borderColor: "#555",
          boxShadow: "none",
          outline: "none",
        },
        "&:before, &:after": {
          display: "none",
        },
      },
      "& input, & .MuiInputBase-input, & .MuiAutocomplete-input": {
        border: "none !important",
        outline: "none !important",
        boxShadow: "none !important",
        background: "transparent !important",
        paddingTop: "0 !important",
        paddingBottom: "0 !important",
        minHeight: "0 !important",
      },
    } as const;

    const [allTicketLogs, setAllTicketLogs] = useState<ToolAuditLogRowLike[]>(
      [],
    );
    const [logs, setLogs] = useState<ToolAuditLogRowLike[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [detail, setDetail] = useState<{
      tool_name: string;
      parameters: string;
      output: string;
      audit_event_type?: "playbook_context";
    } | null>(null);
    const [ticketThreads, setTicketThreads] = useState<TicketThreadOption[]>(
      [],
    );
    const [selectedThreadIds, setSelectedThreadIds] = useState<string[]>([]);
    const hasSetDefaultRef = useRef(false);
    const [loadingThreads, setLoadingThreads] = useState(false);
    const [downloadingCsv, setDownloadingCsv] = useState(false);
    const [hasAnyTicketLogs, setHasAnyTicketLogs] = useState(false);
    const [typeFilter, setTypeFilter] = useState<TypeFilterValue>("all");
    const [statusFilter, setStatusFilter] = useState<StatusFilterValue>("all");
    const [userFilter, setUserFilter] = useState<string[]>([]);
    const [threadSearch, setThreadSearch] = useState("");
    const [userSearch, setUserSearch] = useState("");
    const [sortColumn, setSortColumn] = useState<"time" | "latency" | null>(
      null,
    );
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
    const [collapsedTurnIds, setCollapsedTurnIds] = useState<Record<string, boolean>>({});
    const THREAD_SELECT_ALL_VALUE = "__all_threads__";
    const USER_SELECT_ALL_VALUE = "__all_users__";
    const AUDIT_MODAL_Z_INDEX = AUDIT_MODAL_BASE_Z_INDEX;
    const AUDIT_DROPDOWN_Z_INDEX = AUDIT_MODAL_Z_INDEX + 20;
    const AUDIT_DETAIL_MODAL_Z_INDEX = AUDIT_DROPDOWN_Z_INDEX + 20;

    const currentThreadId = aiThreadId ? String(aiThreadId) : "";
    const currentThreadInList = useMemo(
      () =>
        ticketThreads.some(
          (thread) => String(thread.ai_thread_id) === currentThreadId,
        ),
      [ticketThreads, currentThreadId],
    );
    const threadOptions = useMemo<TicketThreadOption[]>(() => {
      if (currentThreadId && !currentThreadInList) {
        return [
          { ai_thread_id: Number(aiThreadId), created_at: null },
          ...ticketThreads,
        ];
      }
      return ticketThreads;
    }, [aiThreadId, currentThreadId, currentThreadInList, ticketThreads]);
    const allThreadIds = useMemo(
      () => threadOptions.map((thread) => String(thread.ai_thread_id)),
      [threadOptions],
    );
    const allThreadsSelected =
      allThreadIds.length > 0 &&
      allThreadIds.every((id) => selectedThreadIds.includes(id));
    const uniqueUsers = useMemo(() => Array.from(
      new Set(
        allTicketLogs
          .map((r) => r.user_email)
          .filter((email): email is string => email != null && email !== ""),
      ),
    ).sort(), [allTicketLogs]);

    const validUserFilter = useMemo(
      () => userFilter.filter((user) => uniqueUsers.includes(user)),
      [userFilter, uniqueUsers],
    );
    const allUsersSelected =
      uniqueUsers.length > 0 &&
      uniqueUsers.every((u) => validUserFilter.includes(u));
    const someUsersSelected = validUserFilter.length > 0 && !allUsersSelected;
    const userFilterLabel =
      validUserFilter.length === 0
        ? "All"
        : validUserFilter.length === 1
          ? validUserFilter[0]
          : `${validUserFilter.length} users`;

    const selectedThreadsLabel = (() => {
      if (selectedThreadIds.length === 0) {
        return "Select threads";
      }
      if (selectedThreadIds.length === 1) {
        const selected = threadOptions.find(
          (t) => String(t.ai_thread_id) === selectedThreadIds[0],
        );
        if (!selected) return selectedThreadIds[0];
        const isCurrent = String(selected.ai_thread_id) === currentThreadId;
        return isCurrent
          ? "Current thread"
          : formatThreadTimestamp(selected.created_at, threadOptions);
      }
      return `${selectedThreadIds.length} threads selected`;
    })();

    const threadFilterOptions = useMemo<DropdownOption[]>(() => {
      const baseOptions = threadOptions.map((thread) => {
        const threadId = String(thread.ai_thread_id);
        const isCurrent = threadId === currentThreadId;
        return {
          value: threadId,
          label: isCurrent
            ? "Current thread"
            : formatThreadTimestamp(thread.created_at, threadOptions),
        };
      });
      if (baseOptions.length === 0 && currentThreadId) {
        return [
          { value: THREAD_SELECT_ALL_VALUE, label: "Select All" },
          { value: currentThreadId, label: "Current thread" },
        ];
      }
      return [
        { value: THREAD_SELECT_ALL_VALUE, label: "Select All" },
        ...baseOptions,
      ];
    }, [threadOptions, currentThreadId]);

    const selectedThreadFilterOptions = useMemo<DropdownOption[]>(
      () =>
        threadFilterOptions.filter(
          (option) =>
            option.value !== THREAD_SELECT_ALL_VALUE &&
            selectedThreadIds.includes(option.value),
        ),
      [threadFilterOptions, selectedThreadIds],
    );

    const userFilterOptions = useMemo<DropdownOption[]>(
      () => [
        { value: USER_SELECT_ALL_VALUE, label: "Select All" },
        ...uniqueUsers.map((user) => ({ value: user, label: user })),
      ],
      [uniqueUsers],
    );

    const selectedUserFilterOptions = useMemo<DropdownOption[]>(
      () =>
        userFilterOptions.filter(
          (option) =>
            option.value !== USER_SELECT_ALL_VALUE &&
            validUserFilter.includes(option.value),
        ),
      [userFilterOptions, validUserFilter],
    );

    const typeFilterOptions = useMemo<
      Array<DropdownOption & { value: TypeFilterValue }>
    >(
      () => [
        { value: "all", label: "All" },
        { value: "auto", label: "auto" },
        { value: "approval", label: "approval" },
      ],
      [],
    );

    const statusFilterOptions = useMemo<
      Array<DropdownOption & { value: StatusFilterValue }>
    >(
      () => [
        { value: "all", label: "All" },
        { value: "SUCCESS", label: "SUCCESS" },
        { value: "ERROR", label: "ERROR" },
      ],
      [],
    );

    const selectedTypeOption =
      typeFilterOptions.find((option) => option.value === typeFilter) ??
      typeFilterOptions[0];
    const selectedStatusOption =
      statusFilterOptions.find((option) => option.value === statusFilter) ??
      statusFilterOptions[0];

    const filteredLogs = logs.filter((r) => {
      if (
        typeFilter !== "all" &&
        (r.action_permission ?? "auto") !== typeFilter
      )
        return false;
      if (statusFilter !== "all") {
        const isError = isErrorRow(r);
        if (statusFilter === "ERROR" && !isError) return false;
        if (statusFilter === "SUCCESS" && isError) return false;
      }
      if (
        userFilter.length > 0 &&
        (!r.user_email || !userFilter.includes(r.user_email))
      )
        return false;
      return true;
    });

    const sortedLogs = [...filteredLogs].sort((a, b) => {
      if (!sortColumn) return 0;

      if (sortColumn === "time") {
        const aTime = a.start_timestamp
          ? new Date(a.start_timestamp).getTime()
          : 0;
        const bTime = b.start_timestamp
          ? new Date(b.start_timestamp).getTime()
          : 0;
        return sortDirection === "asc" ? aTime - bTime : bTime - aTime;
      }

      if (sortColumn === "latency") {
        const aStart = a.start_timestamp
          ? new Date(a.start_timestamp).getTime()
          : 0;
        const aEnd = a.end_timestamp ? new Date(a.end_timestamp).getTime() : 0;
        const bStart = b.start_timestamp
          ? new Date(b.start_timestamp).getTime()
          : 0;
        const bEnd = b.end_timestamp ? new Date(b.end_timestamp).getTime() : 0;
        const aLatency = aEnd > aStart ? aEnd - aStart : 0;
        const bLatency = bEnd > bStart ? bEnd - bStart : 0;
        return sortDirection === "asc"
          ? aLatency - bLatency
          : bLatency - aLatency;
      }

      return 0;
    });
    const turnNumberById = new Map<string, number>();
    const turnEntryCountById = new Map<string, number>();
    sortedLogs.forEach((row) => {
      const turnId = row.audit_turn_id;
      if (turnId && !turnNumberById.has(turnId)) {
        turnNumberById.set(turnId, turnNumberById.size + 1);
      }
      if (turnId) {
        turnEntryCountById.set(turnId, (turnEntryCountById.get(turnId) || 0) + 1);
      }
    });

    const handleSort = (column: "time" | "latency") => {
      if (sortColumn === column) {
        setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      } else {
        setSortColumn(column);
        setSortDirection("asc");
      }
    };

    useEffect(() => {
      if (!open) {
        setDetail(null);
      }
    }, [open]);

    useEffect(() => {
      if (!open) return;
      // Close any stale page-level menu that may still be mounted behind this modal.
      const frame = window.requestAnimationFrame(() => {
        (document.activeElement as HTMLElement | null)?.blur?.();
        document.dispatchEvent(
          new KeyboardEvent("keydown", {
            key: "Escape",
            code: "Escape",
            bubbles: true,
          }),
        );
      });
      return () => window.cancelAnimationFrame(frame);
    }, [open]);

    useEffect(() => {
      if (userFilter.length > 0) {
        const validUserFilter = userFilter.filter((u) =>
          uniqueUsers.includes(u),
        );
        if (validUserFilter.length !== userFilter.length) {
          setUserFilter(validUserFilter);
        }
      }
    }, [uniqueUsers.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      if (!open) {
        if (!ticketId) {
          setTicketThreads([]);
          setSelectedThreadIds([]);
        }
        return;
      }
      if (!ticketId) {
        setTicketThreads([]);
        return;
      }
      let cancelled = false;
      setLoadingThreads(true);
      chatApi
        .getTicketThreads(ticketId)
        .then(
          (res: {
            threads?: Array<{
              ai_thread_id: number;
              created_at: string | null;
            }>;
          }) => {
            if (!cancelled && res?.threads) {
              setTicketThreads(res.threads);
            }
          },
        )
        .catch(() => {
          if (!cancelled) setTicketThreads([]);
        })
        .finally(() => {
          if (!cancelled) setLoadingThreads(false);
        });
      return () => {
        cancelled = true;
      };
    }, [open, ticketId]);

    useEffect(() => {
      if (!open) {
        setAllTicketLogs([]);
        setHasAnyTicketLogs(false);
        return;
      }

      if (ticketId) {
        let cancelled = false;
        setLoading(true);
        chatApi
          .getTicketToolAuditLogs(ticketId)
          .then((data: unknown) => {
            if (cancelled) return;
            const arr = Array.isArray(data) ? data : [];
            setAllTicketLogs(arr);
            setHasAnyTicketLogs(arr.length > 0);
            setLoading(false);
          })
          .catch((err: any) => {
            if (!cancelled) {
              setError(
                err?.response?.data?.error ??
                  err?.message ??
                  "Failed to load audit logs",
              );
              setHasAnyTicketLogs(false);
              setLoading(false);
            }
          });
        return () => {
          cancelled = true;
        };
      } else if (aiThreadId) {
        let cancelled = false;
        setLoading(true);
        chatApi
          .getToolAuditLogs(aiThreadId)
          .then((data: unknown) => {
            if (cancelled) return;
            const arr = Array.isArray(data) ? data : [];
            setAllTicketLogs(arr);
            setHasAnyTicketLogs(arr.length > 0);
            setLoading(false);
          })
          .catch((err: any) => {
            if (!cancelled) {
              setError(
                err?.response?.data?.error ??
                  err?.message ??
                  "Failed to load audit logs",
              );
              setHasAnyTicketLogs(false);
              setLoading(false);
            }
          });
        return () => {
          cancelled = true;
        };
      } else {
        setAllTicketLogs([]);
        setHasAnyTicketLogs(false);
      }
    }, [open, ticketId, aiThreadId]);

    useEffect(() => {
      if (!open) {
        hasSetDefaultRef.current = false;
        return;
      }
      if (hasSetDefaultRef.current) return;
      const current = aiThreadId ? String(aiThreadId) : null;
      if (!current) return;
      setSelectedThreadIds([current]);
      hasSetDefaultRef.current = true;
    }, [open, aiThreadId]);

    useEffect(() => {
      if (!open) {
        setLogs([]);
        setError(null);
        return;
      }

      if (allTicketLogs.length === 0) {
        setLogs([]);
        return;
      }

      if (!ticketId) {
        const filtered = allTicketLogs;
        filtered.sort((a, b) => {
          const aTime = a.start_timestamp
            ? new Date(a.start_timestamp).getTime()
            : 0;
          const bTime = b.start_timestamp
            ? new Date(b.start_timestamp).getTime()
            : 0;
          return aTime - bTime;
        });
        setLogs(filtered);
        return;
      }

      if (selectedThreadIds.length === 0) {
        setLogs([]);
        return;
      }

      const filtered = allTicketLogs.filter((log) => {
        const logThreadId =
          log.ai_thread_id != null ? String(log.ai_thread_id) : null;
        if (!logThreadId) return false;
        return selectedThreadIds.includes(logThreadId);
      });

      filtered.sort((a, b) => {
        const aTime = a.start_timestamp
          ? new Date(a.start_timestamp).getTime()
          : 0;
        const bTime = b.start_timestamp
          ? new Date(b.start_timestamp).getTime()
          : 0;
        return aTime - bTime;
      });

      setLogs(filtered);
    }, [open, selectedThreadIds, ticketId, allTicketLogs]);

    const handleRefresh = async () => {
      setLoading(true);
      setError(null);
      try {
        if (ticketId) {
          chatApi.refreshTicketAuditCache(ticketId);
          const data = await chatApi.getTicketToolAuditLogs(ticketId);
          const arr = Array.isArray(data) ? data : [];
          setAllTicketLogs(arr);
          setHasAnyTicketLogs(arr.length > 0);
        } else if (aiThreadId) {
          chatApi.invalidateThreadAuditCache(aiThreadId);
          const data = await chatApi.getToolAuditLogs(aiThreadId);
          const arr = Array.isArray(data) ? data : [];
          setAllTicketLogs(arr);
          setHasAnyTicketLogs(arr.length > 0);
        }
      } catch (err: any) {
        setError(
          err?.response?.data?.error ??
            err?.message ??
            "Failed to refresh audit logs",
        );
      } finally {
        setLoading(false);
      }
    };

    const handleDownloadCsv = async () => {
      if (allTicketLogs.length === 0) return;

      setDownloadingCsv(true);
      try {
        const filenamePrefix =
          ticketId || (aiThreadId ? `thread-${aiThreadId}` : "audit-logs");
        downloadAuditLogCsv(allTicketLogs, filenamePrefix);
      } finally {
        setDownloadingCsv(false);
      }
    };

    const handleThreadFilterChange = (
      _event: React.SyntheticEvent,
      nextOptions: DropdownOption[],
      _reason: AutocompleteChangeReason,
      details?: AutocompleteChangeDetails<DropdownOption>,
    ) => {
      if (details?.option?.value === THREAD_SELECT_ALL_VALUE) {
        setSelectedThreadIds(
          allThreadsSelected
            ? currentThreadId
              ? [currentThreadId]
              : []
            : [...allThreadIds],
        );
        return;
      }

      setSelectedThreadIds(
        nextOptions
          .map((option) => option.value)
          .filter((value) => value !== THREAD_SELECT_ALL_VALUE),
      );
    };

    const handleUserFilterChange = (
      _event: React.SyntheticEvent,
      nextOptions: DropdownOption[],
      _reason: AutocompleteChangeReason,
      details?: AutocompleteChangeDetails<DropdownOption>,
    ) => {
      if (details?.option?.value === USER_SELECT_ALL_VALUE) {
        setUserFilter(allUsersSelected ? [] : [...uniqueUsers]);
        return;
      }

      setUserFilter(
        nextOptions
          .map((option) => option.value)
          .filter((value) => value !== USER_SELECT_ALL_VALUE),
      );
    };

    return (
      <>
        <Dialog
          className="neo-tool-audit-dialog"
          open={open}
          onClose={onClose}
          maxWidth="sm"
          fullWidth
          BackdropProps={{
            sx: { zIndex: AUDIT_MODAL_Z_INDEX - 1 },
          }}
          PaperProps={{
            sx: { zIndex: AUDIT_MODAL_Z_INDEX + 1 },
          }}
          sx={{
            ...getDropdownSafeDialogSx({
              borderRadius: 2,
              fontFamily: "var(--theo-font-sans)",
              width: "50vw",
              maxWidth: "95vw",
              minWidth: 360,
              minHeight: 320,
              maxHeight: "90vh",
              resize: "both",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
            }),
            zIndex: AUDIT_MODAL_Z_INDEX,
          }}
        >
          <DialogTitle
            sx={{
              fontFamily: "var(--theo-font-sans)",
              fontWeight: 600,
              color: "#19213D",
              borderBottom: "1px solid #ECECEC",
              position: "relative",
              zIndex: 2,
              overflow: "visible",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              py: 1,
              px: 2,
              pr: 0.5,
            }}
          >
            <span>Audit logs</span>
            <Box sx={{ flex: 1, minWidth: 8 }} />
            {onLiveDebug && (
              <IconButton
                size="small"
                onClick={onLiveDebug}
                disabled={!aiThreadId}
                sx={{ mr: 1 }}
                title="Live debug: follow this thread in a sidebar"
              >
                <BugReportOutlinedIcon />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={handleRefresh}
              disabled={loading || (!ticketId && !aiThreadId)}
              sx={{ mr: 1 }}
              title="Refresh"
            >
              <RefreshIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleDownloadCsv}
              disabled={loading || downloadingCsv || !hasAnyTicketLogs}
              sx={{ mr: 1 }}
              title="Download CSV"
            >
              <DownloadIcon />
            </IconButton>
            {ticketId && (
              <Autocomplete<DropdownOption, true, true, false>
                multiple
                disableCloseOnSelect
                openOnFocus
                inputValue={threadSearch}
                onInputChange={(_, value) => setThreadSearch(value)}
                filterOptions={filterDropdownOptions}
                options={threadFilterOptions}
                value={selectedThreadFilterOptions}
                onChange={handleThreadFilterChange}
                getOptionLabel={(option) => option.label}
                getOptionKey={(option) => option.value}
                isOptionEqualToValue={(option, selected) =>
                  option.value === selected.value
                }
                disabled={loadingThreads || !aiThreadId}
                disableClearable
                renderTags={() => threadSearch ? null : (
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: "#19213D",
                      maxWidth: "100%",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {selectedThreadsLabel}
                  </Typography>
                )}
                renderOption={(props, option) => {
                  const isSelectAll = option.value === THREAD_SELECT_ALL_VALUE;
                  const checked = isSelectAll
                    ? allThreadsSelected
                    : selectedThreadIds.includes(option.value);
                  return (
                    <li {...props}>
                      <Checkbox
                        checked={checked}
                        indeterminate={
                          isSelectAll &&
                          selectedThreadIds.length > 0 &&
                          !allThreadsSelected
                        }
                        size="small"
                        sx={{ mr: 1, py: 0 }}
                      />
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: isSelectAll ? 600 : 400,
                          color: "#19213D",
                        }}
                      >
                        {option.label}
                      </Typography>
                    </li>
                  );
                }}
                slotProps={{
                  popper: {
                    sx: {
                      zIndex: `${AUDIT_DROPDOWN_Z_INDEX} !important`,
                      pointerEvents: "auto",
                    },
                  },
                  paper: {
                    sx: {
                      minWidth: 240,
                      maxHeight: 320,
                      overflow: "auto",
                      mt: 0.5,
                    },
                  },
                }}
                ListboxProps={{
                  sx: {
                    py: 0.25,
                    "& .MuiAutocomplete-option": {
                      minHeight: 32,
                      py: 0.25,
                    },
                  },
                }}
                sx={{
                  minWidth: 240,
                  maxWidth: 340,
                  "& .MuiAutocomplete-input": {
                    p: 0,
                    fontSize: 13,
                  },
                  "& .MuiAutocomplete-inputRoot": {
                    py: 0,
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    sx={auditHeaderSelectInputSx}
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                    }}
                    inputProps={{
                      ...params.inputProps,
                      "aria-label": "Filter audit logs by thread",
                    }}
                  />
                )}
              />
            )}
            <IconButton
              aria-label="Close"
              onClick={onClose}
              size="small"
              sx={{ color: "#666F8D" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              p: 0,
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              position: "relative",
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              ...(logs.length > 0 && !loading && !error
                ? { backgroundColor: "#1a1a1a" }
                : {}),
            }}
          >
            {loading && (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 4,
                }}
              >
                <CircularProgress size={32} sx={{ color: "#666F8D" }} />
              </Box>
            )}
            {!loading && error && (
              <Typography
                sx={{ py: 3, px: 2, color: "#D32F2F", fontFamily: "var(--theo-font-sans)" }}
              >
                {error}
              </Typography>
            )}
            {!loading && !error && logs.length === 0 && (
              <Typography
                sx={{
                  py: 4,
                  px: 2,
                  color: "#666F8D",
                  fontFamily: "var(--theo-font-sans)",
                  textAlign: "center",
                }}
              >
                No tool calls or playbook context deliveries recorded for this thread.
              </Typography>
            )}
            {!loading && !error && logs.length > 0 && (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  maxHeight: "calc(90vh - 112px)",
                  overflowX: "auto",
                  overflowY: "auto",
                  px: 2,
                  pt: 1,
                  pb: 2,
                  ...scrollSx,
                }}
              >
                <Box
                  component="table"
                  sx={{
                    width: "100%",
                    borderCollapse: "collapse",
                    background: "#1a1a1a",
                    color: "#e6edf3",
                    fontFamily: "ui-monospace, monospace",
                    fontSize: "12px",
                    lineHeight: 1.5,
                    "& thead": {
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                      background: "#1a1a1a",
                    },
                    "& th, & td": {
                      padding: "6px 12px",
                      textAlign: "left",
                      borderLeft: "1px solid #444",
                      "&:first-of-type": {
                        borderLeft: "none",
                        paddingLeft: 0,
                      },
                    },
                    "& th": {
                      color: "#888",
                      borderBottom: "1px solid #444",
                      fontWeight: "normal",
                    },
                    "& tbody tr": {
                      borderBottom: "1px solid #2d2d2d",
                    },
                    "& td:nth-of-type(1)": {
                      whiteSpace: "nowrap",
                      minWidth: "160px",
                    },
                    "& td:nth-of-type(2)": { minWidth: "70px" },
                    "& td:nth-of-type(3)": { minWidth: "120px" },
                    "& td:nth-of-type(4)": { minWidth: "64px" },
                    "& td:nth-of-type(5)": { minWidth: "80px" },
                    "& td:nth-of-type(6)": {
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      minWidth: "180px",
                    },
                  }}
                >
                  <thead>
                    <tr>
                      <th>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            cursor: "pointer",
                            userSelect: "none",
                            "&:hover": { color: "#fff" },
                          }}
                          onClick={() => handleSort("time")}
                        >
                          <span>Time</span>
                          {sortColumn === "time" &&
                            (sortDirection === "asc" ? (
                              <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                            ) : (
                              <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                            ))}
                        </Box>
                      </th>
                      <th>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            cursor: "pointer",
                            userSelect: "none",
                            "&:hover": { color: "#fff" },
                          }}
                          onClick={() => handleSort("latency")}
                        >
                          <span>Latency</span>
                          {sortColumn === "latency" &&
                            (sortDirection === "asc" ? (
                              <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                            ) : (
                              <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                            ))}
                        </Box>
                      </th>
                      <th>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <span>User</span>
                          <Autocomplete<DropdownOption, true, true, false>
                            multiple
                            disableCloseOnSelect
                            openOnFocus
                            inputValue={userSearch}
                            onInputChange={(_, value) => setUserSearch(value)}
                            filterOptions={filterDropdownOptions}
                            options={userFilterOptions}
                            value={selectedUserFilterOptions}
                            onChange={handleUserFilterChange}
                            getOptionLabel={(option) => option.label}
                            getOptionKey={(option) => option.value}
                            isOptionEqualToValue={(option, selected) =>
                              option.value === selected.value
                            }
                            disableClearable
                            renderTags={() => userSearch ? null : (
                              <span
                                style={{
                                  fontSize: 11,
                                  color: "#e6edf3",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: "100%",
                                }}
                              >
                                {userFilterLabel}
                              </span>
                            )}
                            renderOption={(props, option) => {
                              const isSelectAll =
                                option.value === USER_SELECT_ALL_VALUE;
                              const checked = isSelectAll
                                ? allUsersSelected
                                : validUserFilter.includes(option.value);
                              return (
                                <li {...props}>
                                  <Checkbox
                                    checked={checked}
                                    indeterminate={
                                      isSelectAll && someUsersSelected
                                    }
                                    size="small"
                                    sx={{ mr: 0.5, py: 0 }}
                                  />
                                  <Typography
                                    sx={{ fontSize: 11, color: "#19213D" }}
                                  >
                                    {option.label}
                                  </Typography>
                                </li>
                              );
                            }}
                            slotProps={{
                              popper: {
                                sx: {
                                  zIndex: `${AUDIT_DROPDOWN_Z_INDEX} !important`,
                                  pointerEvents: "auto",
                                },
                              },
                              paper: {
                                sx: {
                                  minWidth: 180,
                                  maxHeight: 300,
                                  overflow: "auto",
                                  mt: 0.5,
                                },
                              },
                            }}
                            ListboxProps={{
                              sx: {
                                py: 0.25,
                                "& .MuiAutocomplete-option": {
                                  minHeight: 28,
                                  py: 0.25,
                                },
                              },
                            }}
                            sx={{
                              minWidth: 130,
                              "& .MuiAutocomplete-inputRoot": {
                                py: 0,
                                minHeight: 24,
                              },
                              "& .MuiAutocomplete-input": {
                                py: 0,
                                fontSize: 11,
                                color: "#e6edf3",
                                minWidth: 0,
                              },
                              "& .MuiAutocomplete-popupIndicator": {
                                color: "#e6edf3",
                                p: 0,
                              },
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="standard"
                                sx={auditTableFilterInputSx}
                                InputProps={{
                                  ...params.InputProps,
                                  disableUnderline: true,
                                }}
                                inputProps={{
                                  ...params.inputProps,
                                  "aria-label": "Filter audit logs by user",
                                }}
                              />
                            )}
                          />
                        </Box>
                      </th>
                      <th>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <span>Type</span>
                          <Autocomplete<DropdownOption, false, true, false>
                            openOnFocus
                            options={typeFilterOptions}
                            value={selectedTypeOption}
                            onChange={(_, nextOption) =>
                              setTypeFilter(
                                (nextOption?.value as TypeFilterValue) ?? "all",
                              )
                            }
                            getOptionLabel={(option) => option.label}
                            getOptionKey={(option) => option.value}
                            isOptionEqualToValue={(option, selected) =>
                              option.value === selected.value
                            }
                            disableClearable
                            slotProps={{
                              popper: {
                                sx: {
                                  zIndex: `${AUDIT_DROPDOWN_Z_INDEX} !important`,
                                  pointerEvents: "auto",
                                },
                              },
                              paper: {
                                sx: {
                                  mt: 0.5,
                                },
                              },
                            }}
                            ListboxProps={{
                              sx: {
                                py: 0.25,
                                "& .MuiAutocomplete-option": {
                                  minHeight: 28,
                                  py: 0.25,
                                  fontSize: 11,
                                },
                              },
                            }}
                            sx={{
                              minWidth: 90,
                              "& .MuiAutocomplete-inputRoot": {
                                py: 0,
                                minHeight: 24,
                              },
                              "& .MuiAutocomplete-input": {
                                py: 0,
                                fontSize: 11,
                                color: "#e6edf3",
                              },
                              "& .MuiAutocomplete-popupIndicator": {
                                color: "#e6edf3",
                                p: 0,
                              },
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="standard"
                                sx={auditTableFilterInputSx}
                                InputProps={{
                                  ...params.InputProps,
                                  disableUnderline: true,
                                }}
                                inputProps={{
                                  ...params.inputProps,
                                  readOnly: true,
                                }}
                              />
                            )}
                          />
                        </Box>
                      </th>
                      <th>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <span>Status</span>
                          <Autocomplete<DropdownOption, false, true, false>
                            openOnFocus
                            options={statusFilterOptions}
                            value={selectedStatusOption}
                            onChange={(_, nextOption) =>
                              setStatusFilter(
                                (nextOption?.value as StatusFilterValue) ??
                                  "all",
                              )
                            }
                            getOptionLabel={(option) => option.label}
                            getOptionKey={(option) => option.value}
                            isOptionEqualToValue={(option, selected) =>
                              option.value === selected.value
                            }
                            disableClearable
                            slotProps={{
                              popper: {
                                sx: {
                                  zIndex: `${AUDIT_DROPDOWN_Z_INDEX} !important`,
                                  pointerEvents: "auto",
                                },
                              },
                              paper: {
                                sx: {
                                  mt: 0.5,
                                },
                              },
                            }}
                            renderOption={(props, option) => (
                              <li {...props}>
                                <Typography
                                  sx={{
                                    fontSize: 11,
                                    color:
                                      option.value === "SUCCESS"
                                        ? "#4CAF50"
                                        : option.value === "ERROR"
                                          ? "#D32F2F"
                                          : "#19213D",
                                  }}
                                >
                                  {option.label}
                                </Typography>
                              </li>
                            )}
                            ListboxProps={{
                              sx: {
                                py: 0.25,
                                "& .MuiAutocomplete-option": {
                                  minHeight: 28,
                                  py: 0.25,
                                  fontSize: 11,
                                },
                              },
                            }}
                            sx={{
                              minWidth: 100,
                              "& .MuiAutocomplete-inputRoot": {
                                py: 0,
                                minHeight: 24,
                              },
                              "& .MuiAutocomplete-input": {
                                py: 0,
                                fontSize: 11,
                                color: "#e6edf3",
                              },
                              "& .MuiAutocomplete-popupIndicator": {
                                color: "#e6edf3",
                                p: 0,
                              },
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="standard"
                                sx={auditTableFilterInputSx}
                                InputProps={{
                                  ...params.InputProps,
                                  disableUnderline: true,
                                }}
                                inputProps={{
                                  ...params.inputProps,
                                  readOnly: true,
                                }}
                              />
                            )}
                          />
                        </Box>
                      </th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedLogs.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          style={{
                            textAlign: "center",
                            padding: "40px 20px",
                            color: "#666F8D",
                          }}
                        >
                          No audit entries match the selected filters.
                        </td>
                      </tr>
                    ) : (
                      sortedLogs.map((row, index) => {
                        const timestamp = formatTimestamp(row.start_timestamp);
                        const duration = formatDuration(
                          row.start_timestamp,
                          row.end_timestamp,
                        );
                        const failed = isErrorRow(row);
                        const paramsStr = formatParamsAsKeyValue(
                          row.param_json,
                        );
                        // The structured output first, whichever way the row settled. A failed
                        // row used to show `error_message` alone, which is one line where the
                        // action returned a whole result -- the steps that did land, the issue
                        // it hit, the account of each try a retried run made. All of it was
                        // written to output_json and none of it was readable here.
                        //
                        // error_message remains the fallback, because a call that raised before
                        // returning has no output_json and that string is all there is.
                        const outputStr = getFullOutputString(row.output_json) || (failed ? (row.error_message ?? "") : "");
                        const reasoningText = row.reasoning?.trim() ?? "(none)";
                        const turnId = row.audit_turn_id;
                        const previousTurnId = index > 0 ? sortedLogs[index - 1].audit_turn_id : null;
                        const showTurnHeader = !sortColumn && turnId && turnId !== previousTurnId;
                        const turnCollapsed = Boolean(turnId && collapsedTurnIds[turnId]);

                        return (
                          <React.Fragment key={row.id}>
                            {showTurnHeader && (
                              <tr
                                onClick={() =>
                                  setCollapsedTurnIds((current) => ({
                                    ...current,
                                    [turnId]: !current[turnId],
                                  }))
                                }
                                style={{ cursor: "pointer" }}
                              >
                                <td
                                  colSpan={6}
                                  style={{
                                    background: "#252525",
                                    borderTop: "2px solid #705D4B",
                                    color: "#F5E8D5",
                                    fontWeight: 700,
                                    letterSpacing: "0.02em",
                                    padding: "8px 10px",
                                  }}
                                >
                                  {turnCollapsed ? "▸" : "▾"} Turn {turnNumberById.get(turnId)} · started {timestamp} ·{" "}
                                  {turnEntryCountById.get(turnId)} audit entries
                                </td>
                              </tr>
                            )}
                            {!turnCollapsed && (
                              <tr>
                                <td>{timestamp}</td>
                                <td>{duration}</td>
                                <td>{row.user_email ?? "—"}</td>
                                <td>{row.audit_event_type === "playbook_context" ? "Playbook" : getActionTypeLabel(row)}</td>
                                <td>
                                  <span
                                    style={{
                                      color: failed ? "#D32F2F" : "#4CAF50",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {failed ? "ERROR" : "SUCCESS"}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    style={{
                                      cursor: "pointer",
                                      textDecoration: "underline",
                                    }}
                                    onClick={() => {
                                      blurActiveElement();
                                      setDetail({
                                        tool_name: row.tool_name,
                                        parameters: paramsStr || "(none)",
                                        output: outputStr,
                                        audit_event_type: row.audit_event_type,
                                      });
                                    }}
                                  >
                                    {reasoningText}
                                  </span>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </Box>
              </Box>
            )}
          </DialogContent>
        </Dialog>

        <Dialog
          open={Boolean(detail)}
          onClose={() => {
            blurActiveElement();
            setDetail(null);
          }}
          maxWidth="sm"
          fullWidth
          BackdropProps={{
            sx: { zIndex: AUDIT_DETAIL_MODAL_Z_INDEX - 1 },
          }}
          PaperProps={{
            sx: {
              zIndex: AUDIT_DETAIL_MODAL_Z_INDEX + 1,
              borderRadius: 2,
              width: "50vw",
              maxWidth: "95vw",
              minWidth: 360,
              minHeight: 280,
              maxHeight: "90vh",
              resize: "both",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
            },
          }}
          sx={{ zIndex: AUDIT_DETAIL_MODAL_Z_INDEX }}
        >
          <DialogTitle
            sx={{
              fontFamily: "var(--theo-font-sans)",
              fontWeight: 600,
              color: "#19213D",
              borderBottom: "1px solid #ECECEC",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 1,
              px: 2,
              pr: 0.5,
            }}
          >
            <span>{detail?.audit_event_type === "playbook_context" ? "Playbook context details" : "Action details"}</span>
            <IconButton
              aria-label="Close"
              onClick={() => {
                blurActiveElement();
                setDetail(null);
              }}
              size="small"
              sx={{ color: "#666F8D" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              p: 0,
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              backgroundColor: "#1a1a1a",
              color: "#e6edf3",
              fontFamily: "ui-monospace, monospace",
              fontSize: "12px",
            }}
          >
            {detail && (
              <Box
                sx={{
                  px: 2,
                  pt: 1,
                  pb: 2,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  flex: 1,
                }}
              >
                <Box sx={{ mb: 1.5, flexShrink: 0 }}>
                  <Typography
                    component="span"
                    sx={{ color: "#888", display: "block", mb: 0.5 }}
                  >
                  {detail.audit_event_type === "playbook_context" ? "Context event" : "Tool name"}
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      color: "#e6edf3",
                      m: 0,
                    }}
                  >
                    {detail.tool_name}
                  </Box>
                </Box>
                <Box
                  sx={{
                    mb: 1.5,
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    component="span"
                    sx={{ color: "#888", display: "block", mb: 0.5 }}
                  >
                  {detail.audit_event_type === "playbook_context" ? "Delivery evidence" : "Parameters"}
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      flex: 1,
                      minHeight: 0,
                      overflow: "auto",
                      color: "#e6edf3",
                      m: 0,
                      ...scrollSx,
                    }}
                  >
                    {detail.parameters}
                  </Box>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    component="span"
                    sx={{ color: "#888", display: "block", mb: 0.5 }}
                  >
                  {detail.audit_event_type === "playbook_context" ? "Playbook content delivered to Theo" : "Output"}
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      flex: 1,
                      minHeight: 0,
                      overflow: "auto",
                      color: "#e6edf3",
                      m: 0,
                      ...scrollSx,
                    }}
                  >
                    {detail.output || "(none)"}
                  </Box>
                </Box>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  };

  return ToolAuditLogPopup;
};
